/**
 * Fractional positions for manual ordering. A row moved between two neighbours gets the
 * midpoint, so only the moved row is written (important for offline sync).
 */

export const POSITION_STEP = 1024;

/** A position strictly between `before` and `after` (either may be missing). */
export function positionBetween(
  before: number | null | undefined,
  after: number | null | undefined,
): number {
  const hasBefore = typeof before === 'number' && Number.isFinite(before);
  const hasAfter = typeof after === 'number' && Number.isFinite(after);
  if (!hasBefore && !hasAfter) return POSITION_STEP;
  if (!hasBefore) return (after as number) - POSITION_STEP;
  if (!hasAfter) return (before as number) + POSITION_STEP;
  return ((before as number) + (after as number)) / 2;
}

/** Position for appending after the last of `positions`. */
export function positionAfterLast(positions: readonly number[]): number {
  if (positions.length === 0) return POSITION_STEP;
  return Math.max(...positions) + POSITION_STEP;
}

/** Position for inserting before the first of `positions`. */
export function positionBeforeFirst(positions: readonly number[]): number {
  if (positions.length === 0) return POSITION_STEP;
  return Math.min(...positions) - POSITION_STEP;
}

/** True when two neighbours are too close for another midpoint to be distinct. */
export function needsRebalance(before: number, after: number): boolean {
  const mid = (before + after) / 2;
  return mid === before || mid === after || Math.abs(after - before) < 1e-9;
}

/** Evenly spaced positions for `count` rows, used to rebalance a whole group. */
export function rebalancedPositions(count: number): number[] {
  return Array.from({ length: count }, (_, i) => (i + 1) * POSITION_STEP);
}

/**
 * The position for moving an item to `toIndex` within `ordered` (positions of the target
 * group *without* the moved item).
 */
export function positionForIndex(ordered: readonly number[], toIndex: number): number {
  const i = Math.max(0, Math.min(toIndex, ordered.length));
  return positionBetween(ordered[i - 1], ordered[i]);
}
